import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

type DraggableListProps<T> = {
  items: T[];
  getId: (item: T) => string;
  onReorder: (from: number, to: number) => void;
  renderItem: (item: T, index: number, disabled: boolean) => React.ReactNode;
  isDragDisabled?: boolean;
};

export const DraggableList = <T,>({
  items,
  getId,
  onReorder,
  renderItem,
  isDragDisabled,
}: DraggableListProps<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((i) => getId(i) === active.id);
      const newIndex = items.findIndex((i) => getId(i) === over.id);
      onReorder(oldIndex, newIndex);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(getId)}
        strategy={verticalListSortingStrategy}
      >
        {items.map((item, index) => renderItem(item, index, !!isDragDisabled))}
      </SortableContext>
    </DndContext>
  );
};
